import { useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import chroma from 'chroma-js';
import { Suspense } from 'react';
import Load from '~/components/Load';
import { useTheme } from '~/components/Providers/ThemeContext';
import TabSwitch from '~/partials/TabSwitch';
import ThemedImage from '~/partials/ThemedImage';
import { ProgressBar } from '~/primitives/progressbar';
import { useList } from '~/util/hooks/useList';
import { levelsQuery } from '~/util/queries/public/levels';
import { Level } from '~/util/types/levels';
import { NlwTier, nlwTiers } from '~/util/types/nlw';

const Route = getRouteApi('/profile/user/$userid');

const EXCLUDED_TAGS = [
    '2.1',
    '2.2',
    '2.0',
    '1.9',
    '1.8',
    '1.6',
    '1.5',
    'Long',
    'NONG',
];
const EXCLUDED_TAGS_SET = new Set(EXCLUDED_TAGS);

function useCompletedLevels() {
    const list = useList();
    const records = Route.useLoaderData({
        select: ({ profile: { records } }) => records,
    });
    const { data: levels } = useSuspenseQuery(
        levelsQuery({ list, exclude_legacy: true }),
    );

    const levelById = new Map<string, Level>();
    for (const level of levels) {
        levelById.set(level.id, level);
    }

    const completedLevels: Level[] = [];
    const completedLevelIds = new Set<string>();
    for (const record of records) {
        if (completedLevelIds.has(record.level.id)) continue;

        const level = levelById.get(record.level.id);
        if (level) {
            completedLevels.push(level);
            completedLevelIds.add(level.id);
        }
    }

    return completedLevels;
}

function TagBreakdown() {
    const completedLevels = useCompletedLevels();

    const tagCounts = Object.create(null) as Record<string, number>;

    for (const level of completedLevels) {
        for (const tag of level.tags ?? []) {
            if (!tag || EXCLUDED_TAGS_SET.has(tag)) continue;
            tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
        }
    }

    const sorted = Object.entries(tagCounts)
        .sort((tagCountA, tagCountB) => tagCountB[1] - tagCountA[1])
        .slice(0, 10);

    const maxValue = sorted[0]?.[1] ?? 1;

    if (sorted.length === 0) {
        return (
            <p className="text-neutral-400 mt-4">
                Not enough tag data to display breakdown.
            </p>
        );
    }

    return (
        <div className="mt-4 text-left clamp-[px-3-6-clamp]">
            <div className="flex flex-col gap-2">
                {sorted.map(([tag, count]) => {
                    return (
                        <div key={tag} className="flex items-center gap-2">
                            <div className="w-28 text-sm text-neutral-200 truncate">
                                {tag}
                            </div>
                            <ProgressBar
                                value={count}
                                max={maxValue}
                                height="1rem"
                                className="flex-1 bg-background-light"
                                indicatorClassName="bg-primary"
                                ariaLabel={`${tag} tag count`}
                                animateOnRender
                            />
                            <div className="text-xs text-neutral-400 w-8 text-right">
                                {count}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function NlwTierBreakdown() {
    const completedLevels = useCompletedLevels();

    const tierCounts = Object.create(null) as Partial<Record<NlwTier, number>>;

    for (const level of completedLevels) {
        if (!level.nlw_tier) continue;
        tierCounts[level.nlw_tier] = (tierCounts[level.nlw_tier] ?? 0) + 1;
    }

    const tiersWithCounts = nlwTiers.map(
        (tier) => [tier, tierCounts[tier] ?? 0] as const,
    );

    const maxValue = Math.max(...tiersWithCounts.map(([, count]) => count), 1);

    return (
        <div className="mt-4 text-left clamp-[px-3-6-clamp]">
            <div className="flex flex-col gap-2">
                {tiersWithCounts.map(([tier, count]) => {
                    return (
                        <div key={tier} className="flex items-center gap-2">
                            <div className="w-28 text-sm text-neutral-200 truncate">
                                {tier}
                            </div>
                            <ProgressBar
                                value={count}
                                max={maxValue}
                                height="1rem"
                                className="flex-1 bg-background-light"
                                indicatorClassName="bg-primary"
                                ariaLabel={`${tier} NLW tier count`}
                                animateOnRender
                            />
                            <div className="text-xs text-neutral-400 w-8 text-right">
                                {count}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export function ProfileBreakdowns() {
    const { primaryColor } = useTheme();
    const hue = chroma(primaryColor).get('hsl.h');
    const lightness = chroma(primaryColor).get('hsl.l') * 100 + 50;

    const breakdown = Route.useSearch({ select: ({ breakdown }) => breakdown });
    const navigate = Route.useNavigate();

    return (
        <div className="mt-8 text-center clamp-[px-3-6-clamp]">
            <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                <ThemedImage
                    src={`/assets/${breakdown === 'tags' ? 'tagsIcon.webp' : 'statsIcon.webp'}`}
                    alt="breakdown icon"
                    className="inline h-8 mr-2"
                    hueRotation={hue}
                    lightnessCorrection={lightness}
                />
                Records Breakdown
            </h3>

            <TabSwitch
                current={breakdown}
                onChange={(breakdown) => {
                    navigate({
                        search: (prev) => ({
                            ...prev,
                            breakdown,
                        }),
                        replace: true,
                    });
                }}
                options={['tags', 'nlw']}
                optionsDisplay={['Tags', 'NLW Tiers']}
                className="mt-4"
                size="sm"
            >
                <Suspense fallback={<Load />}>
                    <TagBreakdown />
                </Suspense>
                <Suspense fallback={<Load />}>
                    <NlwTierBreakdown />
                </Suspense>
            </TabSwitch>
        </div>
    );
}
